import React, { useEffect } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";

import { useAuthUpdateMe } from "api/generated/endpoints/auth/auth";
import profileFallback from "assets/images/users/avatar-2.jpg";
import useAuth from "global/Auth/use";

import type { RelationData, ProfileData } from "./index";

const faIcon = (iconClass: string) => ({ className = "", size }: { className?: string; size?: number } = {}) => (
  <i className={`${iconClass} ${className}`.trim()} style={size ? { fontSize: size } : undefined} />
);

const CameraIcon = faIcon("fa-solid fa-camera");
const ShieldIcon = faIcon("fa-solid fa-shield-halved");
const SaveIcon = faIcon("fa-solid fa-floppy-disk");
const RotateIcon = faIcon("fa-solid fa-rotate-left");

const addCacheBuster = (url: string, version: number) => {
  if (!version || url === profileFallback) return url;
  return `${url}${url.includes("?") ? "&" : "?"}v=${version}`;
};

type ProfileBoxProps = {
  relationData: RelationData;
  title: string;
};

const ProfileBox: React.FC<ProfileBoxProps> = ({ relationData, title }) => {
  const [profile, setProfile] = React.useState<ProfileData>(relationData.profile);
  const updateProfilePhoto = useAuth(state => state.updateProfilePhoto);

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = React.useState(false);
  const updateMe = useAuthUpdateMe();
  const isSaving = updateMe.isPending;
  const [avatarVersion, setAvatarVersion] = React.useState(0);
  const [formData, setFormData] = React.useState({
    fullName: relationData.profile.fullName || "",
    phone: relationData.profile.phone || "",
  });
  const [formError, setFormError] = React.useState("");

  useEffect(() => {
    setProfile(relationData.profile);
    setFormData({
      fullName: relationData.profile.fullName || "",
      phone: relationData.profile.phone || "",
    });
    setFormError("");
  }, [relationData]);

  const onPickAvatar = async (file: File | null) => {
    if (!file) return;

    setIsUploading(true);
    try {
      const updated = await updateProfilePhoto(file);
      if (updated) setAvatarVersion(v => v + 1);
    } finally {
      setIsUploading(false);
    }
  };

  const profileName = profile?.fullName?.trim() || "Nome não cadastrado";
  const profileEmail = profile?.email?.trim() || "E-mail não cadastrado";
  const avatarSrc = addCacheBuster(profile?.avatarFile?.fileUrl || profileFallback, avatarVersion);

  const syncProfile = (nextProfile: ProfileData) => {
    setProfile(nextProfile);
    useAuth.setState(state => ({
      data: state.data?.relationData
        ? {
            ...state.data,
            relationData: {
              ...state.data.relationData,
              profile: nextProfile,
            },
          }
        : state.data,
    }));
  };

  const handleReset = () => {
    setFormData({
      fullName: profile?.fullName || "",
      phone: profile?.phone || "",
    });
    setFormError("");
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const fullName = formData.fullName.trim();
    const phone = formData.phone.trim();

    if (!fullName) {
      setFormError("Informe o nome completo.");
      return;
    }

    if (isSaving) return;
    setFormError("");

    try {
      const updatedUser = await updateMe.mutateAsync({ data: {
        fullName,
        phone: phone || null,
      } });

      const nextProfile = {
        ...profile,
        fullName: updatedUser.fullName,
        phone: updatedUser.phone || null,
      };

      syncProfile(nextProfile);
      setFormData({
        fullName: updatedUser.fullName,
        phone: updatedUser.phone || "",
      });
    } catch {
      //? O toast de erro (com a mensagem do backend) já vem do mutator
    }
  };

  return (
    <>
      <div className="profile-window">
        <div className="profile-hero">
          <button
            type="button"
            className="profile-avatar"
            onClick={() => !isUploading && fileInputRef.current?.click()}
            title="Alterar foto"
            aria-label="Alterar foto de perfil"
            disabled={isUploading}
          >
            <img src={avatarSrc} alt="Foto de perfil" />

            <span className="profile-avatar-action" aria-hidden="true">
              {CameraIcon({})}
            </span>

            {isUploading && (
              <span className="profile-avatar-loading">
                <span className="spinner-border spinner-border-sm text-light" role="status" aria-label="Salvando foto" />
              </span>
            )}
          </button>

          <div className="profile-heading">
            <span className="profile-role">{title}</span>
            <h2 className="profile-name">{profileName}</h2>
            <span className="profile-badge">
              {ShieldIcon({ size: 13 })}
              Perfil atual
            </span>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            hidden
            onChange={event => {
              const input = event.currentTarget;
              onPickAvatar(input.files?.[0] ?? null);
              input.value = "";
            }}
          />
        </div>

        <Form className="profile-form" onSubmit={handleSave}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="profile-full-name">
                <Form.Label>Nome completo</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.fullName}
                  onChange={event => setFormData(current => ({ ...current, fullName: event.target.value }))}
                  placeholder="Nome completo"
                  disabled={isSaving}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="profile-phone">
                <Form.Label>Telefone</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.phone}
                  onChange={event => setFormData(current => ({ ...current, phone: event.target.value }))}
                  placeholder="(00) 00000-0000"
                  disabled={isSaving}
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group controlId="profile-email">
                <Form.Label>E-mail</Form.Label>
                <Form.Control type="email" value={profileEmail} disabled readOnly />
              </Form.Group>
            </Col>
          </Row>

          {formError && <div className="profile-form-error">{formError}</div>}

          <div className="profile-form-actions">
            <Button variant="outline-secondary" type="button" onClick={handleReset} disabled={isSaving}>
              {RotateIcon({ className: "me-2" })}
              Desfazer
            </Button>
            <Button variant="primary" type="submit" disabled={isSaving}>
              {SaveIcon({ className: "me-2" })}
              {isSaving ? "Salvando..." : "Salvar perfil"}
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
};

export default ProfileBox;
